import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { type Wallpaper, wallpapersCollection, DeviceType } from '../schema';
import { categoryService } from './categoryService';
import { wallpaperValidationService } from './wallpaperValidationService';
import { wallpaperTransformService } from './wallpaperTransformService';
import {
  type DeviceVariation,
  type CreateWallpaperPackData
} from '../../types/device';

/**
 * Service for managing wallpaper packs with multiple device variations
 */
export class WallpaperPackService {
  /**
   * Creates a new wallpaper pack with multiple device variations (admin only)
   * Enhanced to support multiple variations per device
   */
  async createWallpaperPack(packData: CreateWallpaperPackData): Promise<Wallpaper> {
    try {
      // Check authentication and admin permissions
      const { authService } = await import('./authService');
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('You must be signed in to create wallpaper packs');
      }

      const { adminService } = await import('./adminService');
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required to create wallpaper packs');
      }

      // Get the current admin user to set uploadedBy field
      const adminUser = await adminService.verifyAdminUser(currentUser.uid);
      const uploadedBy = adminUser?.userId || adminUser?.id || currentUser.uid;

      // Validate pack data
      const validationErrors = wallpaperValidationService.validateWallpaperPack(packData);
      if (validationErrors.length > 0) {
        throw new Error(`Pack validation failed: ${validationErrors.map(e => e.message).join(', ')}`);
      }

      const now = new Date();

      // Generate search keywords
      const searchKeywords = wallpaperTransformService.generateSearchKeywords(
        packData.title,
        packData.description,
        packData.category,
        packData.tags
      );

      // Process device variations into enhanced Firestore format
      const deviceVariations = wallpaperTransformService.transformVariationsToLegacyFormat(packData.deviceVariations);
      const deviceVariationsList = wallpaperTransformService.transformVariationsToEnhancedFormat(packData.deviceVariations);

      // Create wallpaper pack document with enhanced structure
      const wallpaperPackData: Omit<Wallpaper, 'id'> = {
        title: packData.title,
        description: packData.description,
        category: packData.category,
        tags: packData.tags,
        imageUrl: packData.deviceVariations.find(v => v.device === packData.primaryDevice)?.imageUrl || '',
        thumbnailUrl: deviceVariations[packData.primaryDevice]?.thumbnailUrl || '',
        resolutions: deviceVariations[packData.primaryDevice]?.resolutions || [],
        // Legacy deviceCompatibility field removed - using new device system only
        downloadCount: 0,
        uploadedBy: uploadedBy,
        createdAt: now,
        updatedAt: now,
        searchKeywords,
        // Pack-specific fields
        isWallpaperPack: true,
        deviceVariations,
        deviceVariationsList, // Enhanced format supporting multiple variations per device
        supportedDevices: packData.supportedDevices,
        primaryDevice: packData.primaryDevice,
        packMetadata: {
          totalVariations: packData.deviceVariations.length,
          createdAsType: 'pack',
          lastVariationUpdate: now
        }
      };

      const docRef = doc(wallpapersCollection);
      await setDoc(docRef, wallpaperPackData);

      // Update category count
      if (packData.category) {
        const categories = await categoryService.getAllCategories();
        const category = categories.find(c => c.name === packData.category);
        if (category) {
          await categoryService.incrementWallpaperCount(category.id);
        }
      }

      return {
        id: docRef.id,
        ...wallpaperPackData,
        createdAt: now,
        updatedAt: now,
      };
    } catch (error) {
      console.error('Error creating wallpaper pack:', error);
      throw error instanceof Error ? error : new Error('Failed to create wallpaper pack');
    }
  }

  /**
   * Updates an existing wallpaper pack (admin only)
   */
  async updateWallpaperPack(id: string, packData: Partial<CreateWallpaperPackData>): Promise<void> {
    try {
      // Check authentication and admin permissions
      const { authService } = await import('./authService');
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('You must be signed in to update wallpaper packs');
      }

      const { adminService } = await import('./adminService');
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required to update wallpaper packs');
      }

      const docRef = doc(wallpapersCollection, id);
      const currentDoc = await getDoc(docRef);

      if (!currentDoc.exists()) {
        throw new Error('Wallpaper pack not found');
      }

      const currentData = currentDoc.data() as Wallpaper;
      if (!currentData.isWallpaperPack) {
        throw new Error('Cannot update non-pack wallpaper as pack');
      }

      const updates: Partial<Wallpaper> = {
        updatedAt: new Date()
      };

      // Update basic fields
      if (packData.title) updates.title = packData.title;
      if (packData.description !== undefined) updates.description = packData.description;
      if (packData.category) updates.category = packData.category;
      if (packData.tags) updates.tags = packData.tags;

      // Update device variations if provided
      if (packData.deviceVariations) {
        const validationErrors = wallpaperValidationService.validateWallpaperPack({
          ...currentData,
          ...packData
        } as CreateWallpaperPackData);

        if (validationErrors.length > 0) {
          throw new Error(`Pack validation failed: ${validationErrors.map(e => e.message).join(', ')}`);
        }

        updates.deviceVariations = wallpaperTransformService.transformVariationsToLegacyFormat(packData.deviceVariations);
        updates.deviceVariationsList = wallpaperTransformService.transformVariationsToEnhancedFormat(packData.deviceVariations);
        
        if (updates.packMetadata) {
          updates.packMetadata.totalVariations = packData.deviceVariations.length;
          updates.packMetadata.lastVariationUpdate = new Date();
        }
      }

      if (packData.supportedDevices) {
        updates.supportedDevices = packData.supportedDevices;
        // Legacy deviceCompatibility field removed - using new device system only
      }

      if (packData.primaryDevice) {
        updates.primaryDevice = packData.primaryDevice;
      }

      // Update search keywords if content changed
      if (packData.title || packData.description || packData.tags || packData.category) {
        updates.searchKeywords = wallpaperTransformService.generateSearchKeywords(
          updates.title || currentData.title,
          updates.description !== undefined ? updates.description : currentData.description,
          updates.category || currentData.category,
          updates.tags || currentData.tags
        );
      }

      await updateDoc(docRef, updates);
    } catch (error) {
      console.error('Error updating wallpaper pack:', error);
      throw error instanceof Error ? error : new Error('Failed to update wallpaper pack');
    }
  }

  /**
   * Converts an existing single wallpaper into a wallpaper pack (admin only)
   * Sets pack-specific fields and preserves legacy fields for compatibility.
   */
  async convertSingleToPack(id: string, packData: CreateWallpaperPackData): Promise<void> {
    try {
      // Check authentication and admin permissions
      const { authService } = await import('./authService');
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('You must be signed in to convert wallpapers');
      }

      const { adminService } = await import('./adminService');
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required to convert wallpapers');
      }

      // Fetch existing doc and ensure it's a single wallpaper
      const docRef = doc(wallpapersCollection, id);
      const currentDoc = await getDoc(docRef);
      if (!currentDoc.exists()) {
        throw new Error('Wallpaper not found');
      }

      const currentData = currentDoc.data() as Wallpaper;
      if (currentData.isWallpaperPack) {
        // Already a pack; suggest using update instead
        throw new Error('Wallpaper is already a pack. Use update instead.');
      }

      // Validate new pack data
      const validationErrors = wallpaperValidationService.validateWallpaperPack(packData);
      if (validationErrors.length > 0) {
        throw new Error(`Pack validation failed: ${validationErrors.map(e => e.message).join(', ')}`);
      }

      // Transform variations
      const legacyVariations = wallpaperTransformService.transformVariationsToLegacyFormat(packData.deviceVariations);
      const enhancedVariations = wallpaperTransformService.transformVariationsToEnhancedFormat(packData.deviceVariations);

      // Determine primary assets from primary device variation
      const primaryVar = packData.deviceVariations.find(v => v.device === packData.primaryDevice);
      const imageUrl = primaryVar?.imageUrl || currentData.imageUrl;
      const thumbnailUrl = (legacyVariations[packData.primaryDevice] as any)?.thumbnailUrl || currentData.thumbnailUrl;
      const resolutions = (legacyVariations[packData.primaryDevice] as any)?.resolutions || currentData.resolutions || [];

      // Build updates
      const updates: Partial<Wallpaper> = {
        title: packData.title || currentData.title,
        description: packData.description ?? currentData.description,
        category: packData.category || currentData.category,
        tags: packData.tags ?? currentData.tags,
        imageUrl,
        thumbnailUrl,
        resolutions,
        isWallpaperPack: true,
  deviceVariations: legacyVariations as any,
  deviceVariationsList: enhancedVariations as any,
        supportedDevices: packData.supportedDevices,
        primaryDevice: packData.primaryDevice,
        // Legacy deviceCompatibility field removed - using new device system only
        packMetadata: {
          totalVariations: packData.deviceVariations.length,
          createdAsType: 'single', // mark that this started as a single
          lastVariationUpdate: new Date()
        },
        updatedAt: new Date(),
      };

      // Refresh search keywords if relevant fields changed
      updates.searchKeywords = wallpaperTransformService.generateSearchKeywords(
        updates.title || currentData.title,
        updates.description !== undefined ? updates.description : currentData.description,
        updates.category || currentData.category,
        updates.tags || currentData.tags
      );

      await updateDoc(docRef, updates);
    } catch (error) {
      console.error('Error converting wallpaper to pack:', error);
      throw error instanceof Error ? error : new Error('Failed to convert wallpaper to pack');
    }
  }

  /**
   * Retrieves device variations for a wallpaper pack
   */
  async getWallpaperPackVariations(id: string): Promise<Map<DeviceType, DeviceVariation>> {
    try {
      const wallpaper = await this.getWallpaperById(id);
      if (!wallpaper || !wallpaper.isWallpaperPack) {
        throw new Error('Wallpaper pack not found');
      }

      const variations = new Map<DeviceType, DeviceVariation>();

      if (wallpaper.deviceVariations) {
        Object.entries(wallpaper.deviceVariations).forEach(([deviceType, variationData]) => {
          const device = deviceType as DeviceType;
          variations.set(device, {
            device,
            imageUrl: variationData.imageUrl,
            isValid: true,
            imageInfo: {
              width: variationData.dimensions.width,
              height: variationData.dimensions.height,
              aspectRatio: `${variationData.dimensions.width}:${variationData.dimensions.height}`,
              format: 'unknown',
              fileSize: 'unknown',
              qualityScore: variationData.validationScore
            },
            validationWarnings: [],
            processedUrls: {
              originalUrl: variationData.imageUrl,
              thumbnailUrl: variationData.thumbnailUrl,
              resolutions: variationData.resolutions
            }
          });
        });
      }

      return variations;
    } catch (error) {
      console.error('Error getting wallpaper pack variations:', error);
      throw error instanceof Error ? error : new Error('Failed to get wallpaper pack variations');
    }
  }

  /**
   * Deletes a wallpaper pack and all its variations (admin only)
   */
  async deleteWallpaperPack(id: string): Promise<void> {
    try {
      // Check authentication and admin permissions
      const { authService } = await import('./authService');
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('You must be signed in to delete wallpaper packs');
      }

      const { adminService } = await import('./adminService');
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        throw new Error('Admin privileges required to delete wallpaper packs');
      }

      const docRef = doc(wallpapersCollection, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Wallpaper pack not found');
      }

      const wallpaperData = docSnap.data() as Wallpaper;
      if (!wallpaperData.isWallpaperPack) {
        throw new Error('Cannot delete non-pack wallpaper as pack');
      }

      // Delete the document
      await deleteDoc(docRef);

      // Update category count if category exists
      if (wallpaperData.category) {
        const categories = await categoryService.getAllCategories();
        const category = categories.find(c => c.name === wallpaperData.category);
        if (category) {
          await categoryService.decrementWallpaperCount(category.id);
        }
      }

      console.log(`Wallpaper pack ${id} deleted successfully`);
    } catch (error) {
      console.error('Error deleting wallpaper pack:', error);
      throw error instanceof Error ? error : new Error('Failed to delete wallpaper pack');
    }
  }

  /**
   * Helper method to get wallpaper by ID (reused from query service)
   */
  private async getWallpaperById(id: string): Promise<Wallpaper | null> {
    const { wallpaperQueryService } = await import('./wallpaperQueryService');
    return wallpaperQueryService.getWallpaperById(id);
  }
}

export const wallpaperPackService = new WallpaperPackService();